import {
  useAudioTrack,
  useLocalSessionId,
  useParticipantIds,
  useVideoTrack,
} from "@daily-co/daily-react";
import { MicOff, VideoOff } from "@tamagui/lucide-icons";

import { useGetClinicStatusForPatient } from "@/api/generated";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { XStack, YStack } from "@/components";
import { VideoModes } from "@/store";
import CallPanelTile from "./CallPanelTile";
import RecordingConsentIndicator from "./RecordingConsentIndicator";

type CallPanelProps = {
  videoMode: VideoModes;
};

// Match the `userId` and `displayName` the EMR sets when dialing in the
// LanguageLine interpreter via SIP (see EMR `translatorOptions.ts`). Keep in sync.
const TRANSLATOR_USER_ID = "translator";
const TRANSLATOR_AUDIO_ONLY_NAME = "Interpreter (audio only)";
const TRANSLATOR_NAME = "Interpreter";

const CallPanel = ({ videoMode }: CallPanelProps) => {
  const localSessionId = useLocalSessionId();
  const remoteParticipantId = useParticipantIds({
    filter: (participant) =>
      !participant.local && participant.user_id !== TRANSLATOR_USER_ID,
  }).find(Boolean);
  const translatorParticipantId = useParticipantIds({
    filter: (participant) => participant.user_id === TRANSLATOR_USER_ID,
  }).find(Boolean);
  const isTranslatorAudioOnly =
    useParticipantIds({
      filter: (participant) =>
        participant.user_id === TRANSLATOR_USER_ID &&
        participant.user_name === TRANSLATOR_AUDIO_ONLY_NAME,
    }).length > 0;
  const localAudio = useAudioTrack(localSessionId);
  const localVideo = useVideoTrack(localSessionId);
  const isAudioMuted = localAudio?.isOff;
  const isVideoMuted = localVideo?.isOff;
  const { data: userProfile } = useGetUserProfile();
  const localName = userProfile?.preferredName || "";
  const { data: clinicStatus } = useGetClinicStatusForPatient();
  const remoteParticipantName =
    clinicStatus?.conversation?.curai_clinician_full_name_with_title || "";

  if (videoMode === VideoModes.FLOATING) {
    return (
      <YStack
        flex={1}
        overflow="hidden"
        justifyContent="center"
        backgroundColor="$neutral1000"
        pointerEvents="none"
      >
        {remoteParticipantId && (
          <CallPanelTile
            participantId={remoteParticipantId}
            isSelf={false}
            name={remoteParticipantName || ""}
          />
        )}
        <YStack
          position="absolute"
          top="$100"
          right="$100"
          gap="$100"
          pointerEvents="none"
        >
          <XStack
            justifyContent="center"
            overflow="hidden"
            width="$600"
            height="$600"
          >
            <CallPanelTile
              participantId={localSessionId}
              isSelf
              name={localName || ""}
            />
          </XStack>
          {translatorParticipantId && (
            <XStack
              justifyContent="center"
              overflow="hidden"
              width="$600"
              height="$600"
            >
              <CallPanelTile
                participantId={translatorParticipantId}
                isSelf={false}
                name={TRANSLATOR_NAME}
                audioOnly={isTranslatorAudioOnly}
                compact
              />
            </XStack>
          )}
        </YStack>

        {!!localSessionId && (isAudioMuted || isVideoMuted) && (
          <XStack
            position="absolute"
            bottom="$100"
            left="$100"
            gap="$50"
            pointerEvents="none"
          >
            {isAudioMuted && (
              <MicOff
                aria-label="Muted"
                size="$300"
                color="$dark_red"
                backgroundColor="$neutral700"
                borderRadius="$50"
                padding="$25"
              />
            )}
            {isVideoMuted && (
              <VideoOff
                aria-label="Video off"
                size="$300"
                color="$dark_red"
                backgroundColor="$neutral700"
                borderRadius="$50"
                padding="$25"
              />
            )}
          </XStack>
        )}
      </YStack>
    );
  }

  return (
    <YStack
      flex={1}
      overflow="hidden"
      justifyContent="center"
      backgroundColor="$neutral1000"
      borderTopLeftRadius="$150"
      borderTopRightRadius="$150"
    >
      {remoteParticipantId && (
        <CallPanelTile
          participantId={remoteParticipantId}
          isSelf={false}
          name={remoteParticipantName}
        />
      )}
      <XStack position="absolute" top="$200" left="$200" pointerEvents="none">
        <RecordingConsentIndicator />
      </XStack>
      <YStack
        position="absolute"
        width="$1500"
        height="$2000"
        top="$600"
        right="$200"
        gap="$100"
        overflow="hidden"
        justifyContent="center"
      >
        <CallPanelTile participantId={localSessionId} isSelf name={localName} />
        {translatorParticipantId && (
          <CallPanelTile
            participantId={translatorParticipantId}
            isSelf={false}
            name={TRANSLATOR_NAME}
            audioOnly={isTranslatorAudioOnly}
          />
        )}
      </YStack>
    </YStack>
  );
};

export default CallPanel;
